import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as Diff from 'diff';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import CodeEditor from '../components/CodeEditor';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon, 
  DocumentIcon,
  CodeBracketIcon,
  PaintBrushIcon,
  CheckIcon,
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
  LanguageIcon,
  GlobeAltIcon,
  ClockIcon,
  ArrowsRightLeftIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * File type icon component
 */
const FileIcon = ({ type, isI18n }) => {
  if (isI18n) {
    return <GlobeAltIcon className="h-4 w-4 text-green-500" />;
  }
  switch (type) {
    case 'html':
      return <DocumentIcon className="h-4 w-4 text-orange-500" />;
    case 'css':
      return <PaintBrushIcon className="h-4 w-4 text-blue-500" />;
    case 'javascript':
      return <CodeBracketIcon className="h-4 w-4 text-yellow-500" />;
    case 'json':
      return <CodeBracketIcon className="h-4 w-4 text-green-500" />;
    default:
      return <DocumentIcon className="h-4 w-4 text-gray-500" />;
  }
};

/**
 * Reusable file sidebar for both normal and version-viewing modes
 */
const FileSidebar = ({ files, selectedFileId, onSelectFile, modifiedFiles, isMultiPage }) => {
  const globalFiles = files.filter(f => f.isGlobal);
  const pageFiles = files.filter(f => !f.isGlobal && !f.isI18n);
  const i18nFiles = files.filter(f => f.isI18n);

  return (
    <>
      {isMultiPage && globalFiles.length > 0 && (
        <>
          <div className="text-xs text-gray-500 px-2 py-1 mt-2 mb-1">Global</div>
          {globalFiles.map(file => (
            <button
              key={file.id}
              onClick={() => onSelectFile(file.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                selectedFileId === file.id ? 'bg-primary-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <FileIcon type={file.type} />
              <span className="truncate flex-1">{file.name}</span>
              {modifiedFiles.has(file.id) && <span className="w-2 h-2 rounded-full bg-amber-400" />}
            </button>
          ))}
        </>
      )}
      <div className="text-xs text-gray-500 px-2 py-1 mt-3 mb-1">
        {isMultiPage ? 'Pages' : 'Files'}
      </div>
      {pageFiles.map(file => (
        <button
          key={file.id}
          onClick={() => onSelectFile(file.id)}
          className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
            selectedFileId === file.id ? 'bg-primary-600 text-white' : 'text-gray-300 hover:bg-gray-800'
          }`}
        >
          <FileIcon type={file.type} />
          <span className="truncate flex-1">{file.pageName || file.name}</span>
          {modifiedFiles.has(file.id) && <span className="w-2 h-2 rounded-full bg-amber-400" />}
        </button>
      ))}
      {i18nFiles.length > 0 && (
        <>
          <div className="text-xs text-gray-500 px-2 py-1 mt-3 mb-1 flex items-center gap-1">
            <LanguageIcon className="h-3 w-3" />
            Translations
          </div>
          {i18nFiles.map(file => (
            <button
              key={file.id}
              onClick={() => onSelectFile(file.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors ${
                selectedFileId === file.id ? 'bg-primary-600 text-white' : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <FileIcon type={file.type} isI18n={file.isI18n} />
              <span className="truncate flex-1">
                {file.language?.toUpperCase()}
                {file.isDefaultLanguage && <span className="ml-1 text-xs text-gray-500">(default)</span>}
              </span>
              {modifiedFiles.has(file.id) && <span className="w-2 h-2 rounded-full bg-amber-400" />}
            </button>
          ))}
        </>
      )}
    </>
  );
};

/**
 * Unified diff view component
 */
const DiffView = ({ diff }) => {
  let leftLine = 1;
  let rightLine = 1;

  return (
    <div className="font-mono text-sm">
      {diff.map((part, partIndex) => {
        const lines = part.value.replace(/\n$/, '').split('\n');
        return lines.map((line, lineIndex) => {
          let leftNum = null;
          let rightNum = null;
          if (part.added) { rightNum = rightLine++; }
          else if (part.removed) { leftNum = leftLine++; }
          else { leftNum = leftLine++; rightNum = rightLine++; }

          return (
            <div
              key={`${partIndex}-${lineIndex}`}
              className={`flex ${
                part.added ? 'bg-green-900 bg-opacity-30' : part.removed ? 'bg-red-900 bg-opacity-30' : ''
              }`}
            >
              <span className="w-12 text-right pr-2 text-gray-600 select-none flex-shrink-0 border-r border-gray-800 py-px">
                {leftNum ?? ''}
              </span>
              <span className="w-12 text-right pr-2 text-gray-600 select-none flex-shrink-0 border-r border-gray-800 py-px">
                {rightNum ?? ''}
              </span>
              <span className={`w-5 text-center select-none flex-shrink-0 py-px ${
                part.added ? 'text-green-400' : part.removed ? 'text-red-400' : 'text-gray-700'
              }`}>
                {part.added ? '+' : part.removed ? '-' : ' '}
              </span>
              <span className={`flex-1 px-2 py-px whitespace-pre-wrap break-all ${
                part.added ? 'text-green-300' : part.removed ? 'text-red-300' : 'text-gray-300'
              }`}>
                {line}
              </span>
            </div>
          );
        });
      })}
    </div>
  );
};

/**
 * WebsiteCodeEditor page
 * Allows viewing and editing website HTML, CSS, and JS files
 */
const WebsiteCodeEditor = () => {
  const { websiteId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [websiteData, setWebsiteData] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [modifiedFiles, setModifiedFiles] = useState(new Set());
  const [originalContent, setOriginalContent] = useState({});
  const [previewVisible, setPreviewVisible] = useState(false);
  const [saveDescription, setSaveDescription] = useState('');

  // Version browsing state
  const [versions, setVersions] = useState([]);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [viewingVersionFiles, setViewingVersionFiles] = useState([]);
  const [viewingVersionFileId, setViewingVersionFileId] = useState(null);
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [showVersionPanel, setShowVersionPanel] = useState(false);

  // Compare state
  const [compareMode, setCompareMode] = useState(false);
  const [compareLeftVersion, setCompareLeftVersion] = useState(null);
  const [compareRightVersion, setCompareRightVersion] = useState(null);
  const [compareLeftFiles, setCompareLeftFiles] = useState([]);
  const [compareRightFiles, setCompareRightFiles] = useState([]);
  const [compareSelectedFileId, setCompareSelectedFileId] = useState(null);
  const [loadingCompare, setLoadingCompare] = useState(false);

  // Load website files
  useEffect(() => {
    fetchWebsiteFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteId]);

  const fetchWebsiteFiles = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/files`);
      const { files: loadedFiles, websiteName, currentVersion, isMultiPage, hasI18n, languages, defaultLanguage } = response.data.data;
      
      setWebsiteData({ websiteName, currentVersion, isMultiPage, hasI18n, languages, defaultLanguage });
      setFiles(loadedFiles);
      
      // Store original content for change detection
      const originals = {};
      loadedFiles.forEach(file => {
        originals[file.id] = file.content;
      });
      setOriginalContent(originals);
      
      // Select first file by default
      if (loadedFiles.length > 0) {
        setSelectedFileId(loadedFiles[0].id);
      }
      
      setModifiedFiles(new Set());
    } catch (error) {
      console.error('Error fetching website files:', error);
      toast.error('Failed to load website files');
    } finally {
      setLoading(false);
    }
  };

  // Fetch version history
  const fetchVersions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}`);
      if (response.data.success) {
        setVersions(response.data.data.versions || []);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  }, [websiteId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  // Load a specific version's files (read-only viewing)
  const loadVersionFiles = useCallback(async (versionNumber) => {
    setLoadingVersion(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/websites/${websiteId}/version-files/${versionNumber}`
      );
      if (response.data.success) {
        const vFiles = response.data.data.files;
        setViewingVersionFiles(vFiles);
        setViewingVersion(versionNumber);
        if (vFiles.length > 0) {
          setViewingVersionFileId(vFiles[0].id);
        }
        setCompareMode(false);
      }
    } catch (error) {
      console.error('Error loading version files:', error);
      toast.error('Failed to load version files');
    } finally {
      setLoadingVersion(false);
    }
  }, [websiteId]);

  // Exit version viewing mode
  const exitVersionView = useCallback(() => {
    setViewingVersion(null);
    setViewingVersionFiles([]);
    setViewingVersionFileId(null);
  }, []);

  // Load comparison between two versions
  const loadComparison = useCallback(async (leftVer, rightVer) => {
    if (!leftVer || !rightVer) return;
    setLoadingCompare(true);
    try {
      const [leftRes, rightRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/version-files/${leftVer}`),
        axios.get(`${API_BASE_URL}/api/admin/websites/${websiteId}/version-files/${rightVer}`),
      ]);
      const lFiles = leftRes.data.data?.files || [];
      const rFiles = rightRes.data.data?.files || [];
      setCompareLeftFiles(lFiles);
      setCompareRightFiles(rFiles);

      const allIds = new Set([...lFiles.map(f => f.id), ...rFiles.map(f => f.id)]);
      const firstChanged = [...allIds].find(id => {
        const l = lFiles.find(f => f.id === id);
        const r = rFiles.find(f => f.id === id);
        return !l || !r || l.content !== r.content;
      });
      setCompareSelectedFileId(firstChanged || [...allIds][0] || null);
    } catch (error) {
      console.error('Error loading comparison:', error);
      toast.error('Failed to load comparison');
    } finally {
      setLoadingCompare(false);
    }
  }, [websiteId]);

  // Enter compare mode
  const enterCompareMode = useCallback(() => {
    exitVersionView();
    setCompareMode(true);
    if (versions.length >= 2) {
      const sorted = [...versions].sort((a, b) => b.version - a.version);
      const right = sorted[0].version;
      const left = sorted[1].version;
      setCompareLeftVersion(left);
      setCompareRightVersion(right);
      loadComparison(left, right);
    }
  }, [versions, exitVersionView, loadComparison]);

  const exitCompareMode = useCallback(() => {
    setCompareMode(false);
    setCompareLeftFiles([]);
    setCompareRightFiles([]);
    setCompareSelectedFileId(null);
  }, []);

  // Compute diff for compare mode
  const compareDiff = useMemo(() => {
    if (!compareMode || !compareSelectedFileId) return null;
    const lFile = compareLeftFiles.find(f => f.id === compareSelectedFileId);
    const rFile = compareRightFiles.find(f => f.id === compareSelectedFileId);
    const leftContent = lFile?.content || '';
    const rightContent = rFile?.content || '';
    if (leftContent === rightContent) return null;
    return Diff.diffLines(leftContent, rightContent);
  }, [compareMode, compareSelectedFileId, compareLeftFiles, compareRightFiles]);

  // Compare file statuses
  const compareFileStatuses = useMemo(() => {
    if (!compareMode) return {};
    const allIds = new Set([...compareLeftFiles.map(f => f.id), ...compareRightFiles.map(f => f.id)]);
    const statuses = {};
    for (const id of allIds) {
      const l = compareLeftFiles.find(f => f.id === id);
      const r = compareRightFiles.find(f => f.id === id);
      if (!l) statuses[id] = 'added';
      else if (!r) statuses[id] = 'removed';
      else if (l.content !== r.content) statuses[id] = 'modified';
      else statuses[id] = 'unchanged';
    }
    return statuses;
  }, [compareMode, compareLeftFiles, compareRightFiles]);

  const compareAllFileIds = useMemo(() => {
    if (!compareMode) return [];
    return [...new Set([...compareLeftFiles.map(f => f.id), ...compareRightFiles.map(f => f.id)])];
  }, [compareMode, compareLeftFiles, compareRightFiles]);

  // Get currently selected file
  const selectedFile = files.find(f => f.id === selectedFileId);

  // Handle file content change
  const handleContentChange = useCallback((newContent) => {
    if (!selectedFileId) return;

    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.id === selectedFileId 
          ? { ...file, content: newContent }
          : file
      )
    );

    // Check if content differs from original
    setModifiedFiles(prev => {
      const newSet = new Set(prev);
      if (newContent !== originalContent[selectedFileId]) {
        newSet.add(selectedFileId);
      } else {
        newSet.delete(selectedFileId);
      }
      return newSet;
    });
  }, [selectedFileId, originalContent]);

  // Save all modified files
  const handleSave = async () => {
    if (modifiedFiles.size === 0) {
      toast('No changes to save', { icon: 'ℹ️' });
      return;
    }

    try {
      setSaving(true);
      
      const modifiedFileData = files.filter(f => modifiedFiles.has(f.id));
      
      const response = await axios.put(`${API_BASE_URL}/api/admin/websites/${websiteId}/files`, {
        files: modifiedFileData,
        description: saveDescription || `Manual code edit - ${modifiedFileData.length} file(s) modified`
      });

      if (response.data.success) {
        toast.success(`Changes saved! Version ${response.data.data.version} created.`);
        
        // Update original content to reflect saved state
        const newOriginals = { ...originalContent };
        modifiedFileData.forEach(file => {
          newOriginals[file.id] = file.content;
        });
        setOriginalContent(newOriginals);
        setModifiedFiles(new Set());
        setSaveDescription('');
        
        // Update version in header
        setWebsiteData(prev => ({
          ...prev,
          currentVersion: response.data.data.version
        }));
      }
    } catch (error) {
      console.error('Error saving files:', error);
      toast.error(error.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Discard all changes
  const handleDiscard = () => {
    if (modifiedFiles.size === 0) return;

    if (!window.confirm('Are you sure you want to discard all changes?')) {
      return;
    }

    setFiles(prevFiles => 
      prevFiles.map(file => ({
        ...file,
        content: originalContent[file.id]
      }))
    );
    setModifiedFiles(new Set());
    toast.success('Changes discarded');
  };

  // Get language for Monaco editor
  const getEditorLanguage = (type) => {
    switch (type) {
      case 'html': return 'html';
      case 'css': return 'css';
      case 'javascript': return 'javascript';
      case 'json': return 'json';
      default: return 'html';
    }
  };

  // Generate preview HTML with injected CSS and JS
  const getPreviewHtml = () => {
    let html = '';
    let css = '';
    let js = '';

    if (!websiteData?.isMultiPage) {
      // Single-page format
      const htmlFile = files.find(f => f.type === 'html');
      const cssFile = files.find(f => f.type === 'css');
      const jsFile = files.find(f => f.type === 'javascript');
      
      html = htmlFile?.content || '';
      css = cssFile?.content || '';
      js = jsFile?.content || '';
    } else {
      // Multi-page: preview selected HTML page or index
      html = selectedFile?.type === 'html' 
        ? selectedFile.content 
        : files.find(f => f.type === 'html' && !f.isGlobal)?.content || '';
      
      const cssFile = files.find(f => f.isGlobal && f.type === 'css');
      const jsFile = files.find(f => f.isGlobal && f.type === 'javascript');
      
      css = cssFile?.content || '';
      js = jsFile?.content || '';
    }

    if (!html) return '<html><body><p>No HTML content to preview</p></body></html>';

    // Create style and script tags to inject
    const styleTag = css ? `<style id="zappy-preview-styles">\n${css}\n</style>` : '';
    const scriptTag = js ? `<script id="zappy-preview-scripts">\n${js}\n</script>` : '';

    // Remove any existing external stylesheet links that might cause loading issues in preview
    // (Keep inline styles and Font Awesome)
    let processedHtml = html;

    // Inject CSS - try multiple strategies
    if (styleTag) {
      if (processedHtml.includes('</head>')) {
        processedHtml = processedHtml.replace('</head>', `${styleTag}\n</head>`);
      } else if (processedHtml.includes('<body')) {
        processedHtml = processedHtml.replace(/<body/i, `${styleTag}\n<body`);
      } else {
        // No head or body tag - prepend style
        processedHtml = styleTag + '\n' + processedHtml;
      }
    }

    // Inject JS - try multiple strategies  
    if (scriptTag) {
      if (processedHtml.includes('</body>')) {
        processedHtml = processedHtml.replace('</body>', `${scriptTag}\n</body>`);
      } else if (processedHtml.includes('</html>')) {
        processedHtml = processedHtml.replace('</html>', `${scriptTag}\n</html>`);
      } else {
        // No body or html closing tag - append script
        processedHtml = processedHtml + '\n' + scriptTag;
      }
    }

    return processedHtml;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="py-12">
          <LoadingSpinner />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout fullWidth>
      <div className="flex flex-col h-[calc(100vh-64px)]">
        {/* Header Bar */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/websites/${websiteId}`)}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span className="hidden sm:inline">Back to Details</span>
            </button>
            
            <div className="h-6 w-px bg-gray-700" />
            
            <div>
              <h1 className="text-lg font-semibold text-white">
                {websiteData?.websiteName || 'Code Editor'}
              </h1>
              <p className="text-xs text-gray-500">
                {viewingVersion ? (
                  <>
                    <span className="text-blue-400">Browsing Version {viewingVersion}</span>
                    <span className="text-gray-600"> • Active: v{websiteData?.currentVersion || 1}</span>
                    <span> • {viewingVersionFiles.length} files</span>
                  </>
                ) : compareMode ? (
                  <span className="text-purple-400">
                    Comparing v{compareLeftVersion} → v{compareRightVersion}
                  </span>
                ) : (
                  <>
                    Version {websiteData?.currentVersion || 1} • {files.length} files
                    {websiteData?.hasI18n && (
                      <span className="ml-2 text-green-400">
                        • {websiteData.languages?.length || 0} languages
                      </span>
                    )}
                  </>
                )}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {modifiedFiles.size > 0 && (
              <span className="text-xs text-amber-400">
                {modifiedFiles.size} unsaved change{modifiedFiles.size > 1 ? 's' : ''}
              </span>
            )}

            {/* Version browsing badge */}
            {viewingVersion && (
              <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-600 bg-opacity-20 text-blue-400 text-xs font-medium rounded-full border border-blue-500 border-opacity-30">
                <ClockIcon className="h-3.5 w-3.5" />
                Viewing v{viewingVersion} (read-only)
                <button onClick={exitVersionView} className="ml-1 hover:text-white">
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            )}

            {compareMode && (
              <span className="flex items-center gap-1.5 px-3 py-1 bg-purple-600 bg-opacity-20 text-purple-400 text-xs font-medium rounded-full border border-purple-500 border-opacity-30">
                <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
                Compare Mode
                <button onClick={exitCompareMode} className="ml-1 hover:text-white">
                  <XMarkIcon className="h-3.5 w-3.5" />
                </button>
              </span>
            )}

            {!viewingVersion && !compareMode && (
              <>
                <button
                  onClick={() => { setShowVersionPanel(!showVersionPanel); exitCompareMode(); }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                    showVersionPanel
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <ClockIcon className="h-4 w-4" />
                  Versions
                </button>

                {versions.length >= 2 && (
                  <button
                    onClick={enterCompareMode}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-300 rounded-md text-sm hover:bg-gray-600 transition-colors"
                  >
                    <ArrowsRightLeftIcon className="h-4 w-4" />
                    Compare
                  </button>
                )}
              </>
            )}
            
            <button
              onClick={() => setPreviewVisible(!previewVisible)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                previewVisible 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <EyeIcon className="h-4 w-4" />
              Preview
            </button>
            
            <button
              onClick={handleDiscard}
              disabled={modifiedFiles.size === 0}
              className="flex items-center gap-2 px-3 py-1.5 bg-gray-700 text-gray-300 rounded-md text-sm hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XMarkIcon className="h-4 w-4" />
              Discard
            </button>
            
            <button
              onClick={handleSave}
              disabled={saving || modifiedFiles.size === 0}
              className="flex items-center gap-2 px-4 py-1.5 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4" />
                  Save & Create Version
                </>
              )}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Version Panel (slide-out) */}
          {showVersionPanel && !viewingVersion && !compareMode && (
            <div className="w-72 bg-gray-900 border-r border-gray-700 flex flex-col flex-shrink-0">
              <div className="flex items-center justify-between p-3 border-b border-gray-700">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <ClockIcon className="h-4 w-4" />
                  Version History
                </h2>
                <button
                  onClick={() => setShowVersionPanel(false)}
                  className="text-gray-500 hover:text-gray-300"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {versions.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">No versions found</p>
                ) : (
                  versions.map(v => (
                    <div
                      key={v.id || v.version}
                      className={`mb-1 rounded-lg p-3 transition-colors ${
                        v.version === websiteData?.currentVersion
                          ? 'bg-primary-600 bg-opacity-20 border border-primary-500 border-opacity-30'
                          : 'hover:bg-gray-800 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-semibold text-gray-200">
                          Version {v.version}
                        </span>
                        {v.version === websiteData?.currentVersion && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary-500 text-white">
                            Current
                          </span>
                        )}
                      </div>
                      {v.description && (
                        <p className="text-xs text-gray-400 mb-2 line-clamp-2">
                          {v.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-500">
                          {new Date(v.created_at).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </span>
                        {v.version !== websiteData?.currentVersion && (
                          <button
                            onClick={() => { loadVersionFiles(v.version); setShowVersionPanel(false); }}
                            disabled={loadingVersion}
                            className="text-xs font-medium text-blue-400 hover:text-blue-300 flex items-center gap-1"
                          >
                            <CodeBracketIcon className="h-3 w-3" />
                            View Code
                          </button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* File Sidebar */}
          {!compareMode && (
            <div className="w-64 bg-gray-900 border-r border-gray-700 flex flex-col">
              <div className="p-3 border-b border-gray-700">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider">
                  {viewingVersion ? `v${viewingVersion} Files` : 'Files'}
                </h2>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2">
                <FileSidebar
                  files={viewingVersion ? viewingVersionFiles : files}
                  selectedFileId={viewingVersion ? viewingVersionFileId : selectedFileId}
                  onSelectFile={viewingVersion ? setViewingVersionFileId : setSelectedFileId}
                  modifiedFiles={viewingVersion ? new Set() : modifiedFiles}
                  isMultiPage={websiteData?.isMultiPage}
                />
              </div>
              
              {/* Save Description Input */}
              {!viewingVersion && modifiedFiles.size > 0 && (
                <div className="p-3 border-t border-gray-700">
                  <label className="block text-xs text-gray-500 mb-1">
                    Change description (optional)
                  </label>
                  <input
                    type="text"
                    value={saveDescription}
                    onChange={(e) => setSaveDescription(e.target.value)}
                    placeholder="Describe your changes..."
                    className="w-full px-2 py-1.5 bg-gray-800 border border-gray-700 rounded text-sm text-gray-300 placeholder-gray-500 focus:outline-none focus:border-primary-500"
                  />
                </div>
              )}
            </div>
          )}

          {/* Compare mode sidebar */}
          {compareMode && (
            <div className="w-64 bg-gray-900 border-r border-gray-700 flex flex-col flex-shrink-0">
              <div className="p-3 border-b border-gray-700">
                <h2 className="text-sm font-medium text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <ArrowsRightLeftIcon className="h-4 w-4" />
                  Compare
                </h2>
              </div>
              <div className="p-3 border-b border-gray-700 space-y-2">
                <div>
                  <label className="block text-[10px] text-gray-500 uppercase mb-1">Old version</label>
                  <select
                    value={compareLeftVersion || ''}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setCompareLeftVersion(v);
                      loadComparison(v, compareRightVersion);
                    }}
                    className="w-full bg-gray-800 text-gray-200 text-sm border border-gray-700 rounded px-2 py-1.5 focus:outline-none focus:border-primary-500"
                  >
                    {versions.map(v => (
                      <option key={v.version} value={v.version}>
                        v{v.version}{v.version === websiteData?.currentVersion ? ' (current)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 uppercase mb-1">New version</label>
                  <select
                    value={compareRightVersion || ''}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setCompareRightVersion(v);
                      loadComparison(compareLeftVersion, v);
                    }}
                    className="w-full bg-gray-800 text-gray-200 text-sm border border-gray-700 rounded px-2 py-1.5 focus:outline-none focus:border-primary-500"
                  >
                    {versions.map(v => (
                      <option key={v.version} value={v.version}>
                        v{v.version}{v.version === websiteData?.currentVersion ? ' (current)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-2">
                {compareAllFileIds.map(fileId => {
                  const file = compareRightFiles.find(f => f.id === fileId) || compareLeftFiles.find(f => f.id === fileId);
                  const status = compareFileStatuses[fileId];
                  return (
                    <button
                      key={fileId}
                      onClick={() => setCompareSelectedFileId(fileId)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm transition-colors mb-0.5 ${
                        compareSelectedFileId === fileId
                          ? 'bg-primary-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800'
                      }`}
                    >
                      <FileIcon type={file?.type} isI18n={file?.isI18n} />
                      <span className="truncate flex-1">{file?.name || fileId}</span>
                      {status === 'added' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-900 text-green-300">A</span>}
                      {status === 'removed' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-900 text-red-300">D</span>}
                      {status === 'modified' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-900 text-yellow-300">M</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Editor Area */}
          <div className={`flex-1 flex ${previewVisible && !compareMode ? 'flex-row' : ''}`}>
            {/* Compare Diff View */}
            {compareMode ? (
              <div className="w-full bg-gray-900 flex flex-col h-full">
                {loadingCompare ? (
                  <div className="flex-1 flex items-center justify-center">
                    <LoadingSpinner />
                  </div>
                ) : compareSelectedFileId ? (
                  <>
                    <div className="flex items-center px-4 py-2 bg-gray-800 border-b border-gray-700 gap-4">
                      <FileIcon
                        type={(compareRightFiles.find(f => f.id === compareSelectedFileId) || compareLeftFiles.find(f => f.id === compareSelectedFileId))?.type}
                      />
                      <span className="text-sm text-gray-300 font-mono">
                        {(compareRightFiles.find(f => f.id === compareSelectedFileId) || compareLeftFiles.find(f => f.id === compareSelectedFileId))?.name}
                      </span>
                      {compareFileStatuses[compareSelectedFileId] === 'unchanged' && (
                        <span className="text-gray-500 text-xs ml-auto">No changes</span>
                      )}
                      {compareFileStatuses[compareSelectedFileId] !== 'unchanged' && (
                        <span className="ml-auto">
                          {compareFileStatuses[compareSelectedFileId] === 'added' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-900 text-green-300">Added</span>}
                          {compareFileStatuses[compareSelectedFileId] === 'removed' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-900 text-red-300">Deleted</span>}
                          {compareFileStatuses[compareSelectedFileId] === 'modified' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-900 text-yellow-300">Modified</span>}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 overflow-auto">
                      {compareDiff === null ? (
                        <div className="flex items-center justify-center h-full text-gray-500">
                          <div className="text-center">
                            <ChevronUpDownIcon className="w-8 h-8 mx-auto mb-2 text-gray-600" />
                            <p>Files are identical</p>
                          </div>
                        </div>
                      ) : (
                        <DiffView diff={compareDiff} />
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-500">
                    Select a file to see the diff
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Code Editor (normal or version viewing) */}
                <div className={`${previewVisible ? 'w-1/2 border-r border-gray-700' : 'w-full'} bg-gray-900`}>
                  {(() => {
                    const activeFile = viewingVersion
                      ? viewingVersionFiles.find(f => f.id === viewingVersionFileId)
                      : selectedFile;
                    const isReadOnly = !!viewingVersion;

                    if (!activeFile) {
                      return (
                        <div className="h-full flex items-center justify-center text-gray-500">
                          {loadingVersion ? <LoadingSpinner /> : 'Select a file to edit'}
                        </div>
                      );
                    }

                    return (
                      <div className="h-full flex flex-col">
                        <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 border-b border-gray-700">
                          <FileIcon type={activeFile.type} isI18n={activeFile.isI18n} />
                          <span className="text-sm text-gray-300">
                            {activeFile.isI18n
                              ? `${activeFile.language?.toUpperCase()} Translations`
                              : activeFile.name}
                          </span>
                          {activeFile.isI18n && activeFile.isDefaultLanguage && (
                            <span className="text-xs text-gray-500">(default)</span>
                          )}
                          {!isReadOnly && modifiedFiles.has(activeFile.id) && (
                            <span className="text-amber-400">•</span>
                          )}
                          {isReadOnly && (
                            <span className="ml-auto text-xs text-gray-600">Read-only</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <CodeEditor
                            key={isReadOnly ? `ro-${viewingVersion}-${activeFile.id}` : activeFile.id}
                            value={activeFile.content}
                            onChange={isReadOnly ? undefined : handleContentChange}
                            language={getEditorLanguage(activeFile.type)}
                            theme="vs-dark"
                            readOnly={isReadOnly}
                          />
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Preview Panel */}
                {previewVisible && (
                  <div className="w-1/2 bg-white">
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
                        <span className="text-sm text-gray-600 font-medium">Preview</span>
                        <button
                          onClick={() => setPreviewVisible(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <iframe
                          srcDoc={getPreviewHtml()}
                          title="Preview"
                          className="w-full h-full border-0"
                          sandbox="allow-scripts allow-same-origin"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default WebsiteCodeEditor;

